import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ClientLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2, User, Phone, Save, Loader2, Server, Upload,
  ImageIcon, Plus, Trash2, MapPin, Users, Briefcase, Mail, Layers, KeyRound, Shield,
  Smartphone, ShieldCheck, ShieldOff, Copy, RefreshCw, Eye, EyeOff
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_CONTACT = { name: '', title: '', department: '', email: '', phone: '', bcp_role: '' };
const EMPTY_LOCATION = { name: '', address: '', city: '', state: '', zip: '', type: 'Branch Office' };
const EMPTY_DEPARTMENT = { name: '', description: '', email: '' };

export default function ProfilePage() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Account security
  const [emailForm, setEmailForm] = useState({ new_email: '', password: '' });
  const [emailSaving, setEmailSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '', contact_name: '', title: '', phone: '',
    direct_phone: '', mobile_phone: '',
    company_address: '', company_city: '', company_state: '', company_zip: '',
    employee_count: '', employees_in_office: '', employees_remote: '', employees_hybrid: '',
    msp_company_name: '', msp_contract_number: '', msp_primary_contact: '',
    msp_primary_phone: '', msp_primary_email: '', msp_support_phone: '',
    msp_support_email: '', msp_portal_url: '', vendor_email: ''
  });
  const [keyContacts, setKeyContacts] = useState([]);
  const [officeLocations, setOfficeLocations] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (user) {
      setFormData({
        company_name: user.company_name || '',
        contact_name: user.contact_name || '',
        title: user.title || '',
        phone: user.phone || '',
        direct_phone: user.direct_phone || '',
        mobile_phone: user.mobile_phone || '',
        company_address: user.company_address || '',
        company_city: user.company_city || '',
        company_state: user.company_state || '',
        company_zip: user.company_zip || '',
        employee_count: user.employee_count || '',
        employees_in_office: user.employees_in_office || '',
        employees_remote: user.employees_remote || '',
        employees_hybrid: user.employees_hybrid || '',
        msp_company_name: user.msp_company_name || '',
        msp_contract_number: user.msp_contract_number || '',
        msp_primary_contact: user.msp_primary_contact || '',
        msp_primary_phone: user.msp_primary_phone || '',
        msp_primary_email: user.msp_primary_email || '',
        msp_support_phone: user.msp_support_phone || '',
        msp_support_email: user.msp_support_email || '',
        msp_portal_url: user.msp_portal_url || '',
        vendor_email: user.vendor_email || ''
      });
      setKeyContacts(user.key_contacts?.length ? user.key_contacts : []);
      setOfficeLocations(user.office_locations?.length ? user.office_locations : []);
      setDepartments(user.departments?.length ? user.departments : []);
      if (user.logo_path) loadLogo(user.logo_path);
    }
  }, [user]);

  const loadLogo = async (path) => {
    try {
      const r = await axios.get(`${API_URL}/files/${path}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      setLogoUrl(URL.createObjectURL(r.data));
    } catch (err) { /* silently fail */ }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or GIF image'); return;
    }
    if (file.size > 2 * 1024 * 1024) { toast.error('File size must be under 2MB'); return; }

    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/profile/logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (r.data.logo_path) { await loadLogo(r.data.logo_path); toast.success('Logo uploaded!'); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload logo'); }
    finally { setLogoUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API_URL}/auth/profile`, {
        ...formData,
        key_contacts: keyContacts,
        office_locations: officeLocations,
        departments: departments
      });
      toast.success('Profile updated successfully!');
      window.location.reload();
    } catch (err) { toast.error('Failed to update profile'); }
    finally { setLoading(false); }
  };

  const updateContact = (idx, field, value) => {
    const updated = [...keyContacts];
    updated[idx] = { ...updated[idx], [field]: value };
    setKeyContacts(updated);
  };

  const updateLocation = (idx, field, value) => {
    const updated = [...officeLocations];
    updated[idx] = { ...updated[idx], [field]: value };
    setOfficeLocations(updated);
  };

  const updateDepartment = (idx, field, value) => {
    const updated = [...departments];
    updated[idx] = { ...updated[idx], [field]: value };
    setDepartments(updated);
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email || !emailForm.password) { toast.error('Fill in both fields'); return; }
    setEmailSaving(true);
    try {
      await axios.put(`${API_URL}/auth/change-email`, emailForm);
      toast.success('Email updated! Reloading...');
      setEmailForm({ new_email: '', password: '' });
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change email');
    } finally { setEmailSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('Passwords do not match'); return; }
    if (pwForm.new_password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setPwSaving(true);
    try {
      await axios.put(`${API_URL}/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      });
      toast.success('Password changed successfully!');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwSaving(false); }
  };

  return (
    <ClientLayout>
      <div className="page-container py-8">
        <PageHeader
          title="Company Profile"
          description="Manage your company information, key contacts, and MSP/vendor details. This data auto-populates into compliance documents."
          breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Profile' }]}
        />

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Company Logo */}
          <Section icon={ImageIcon} title="Company Logo" subtitle="Your logo will appear on PDF cover pages">
            <div className="flex items-center gap-8">
              <div className="w-40 h-40 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50 overflow-hidden hover:border-[#FF6B35]/50 transition-colors" data-testid="logo-preview-area">
                {logoUrl ? (
                  <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain p-2" data-testid="logo-preview-image" />
                ) : (
                  <div className="text-center p-4">
                    <ImageIcon className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs text-slate-400">No logo uploaded</p>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={logoUploading} className="flex items-center gap-2" data-testid="upload-logo-btn">
                  {logoUploading ? <><Loader2 className="h-4 w-4 animate-spin" />Uploading...</> : <><Upload className="h-4 w-4" />{logoUrl ? 'Change Logo' : 'Upload Logo'}</>}
                </Button>
                <p className="text-xs text-slate-500">JPEG, PNG, WebP, or GIF. Max 2MB.</p>
              </div>
            </div>
          </Section>

          {/* Primary Contact */}
          <Section icon={User} title="Primary Contact" subtitle="Your personal information and contact details">
            <div className="grid md:grid-cols-3 gap-6">
              <Field label="Full Name" value={formData.contact_name} onChange={v => set('contact_name', v)} placeholder="John Smith" testId="profile-contact-name" />
              <Field label="Title / Position" value={formData.title} onChange={v => set('title', v)} placeholder="e.g., CEO, IT Director" testId="profile-title" />
              <Field label="Email" value={user?.email || ''} disabled />
              <Field label="Main Phone" value={formData.phone} onChange={v => set('phone', v)} placeholder="(555) 123-4567" testId="profile-phone" type="tel" />
              <Field label="Direct Phone" value={formData.direct_phone} onChange={v => set('direct_phone', v)} placeholder="(555) 123-4568" testId="profile-direct-phone" type="tel" />
              <Field label="Mobile Phone" value={formData.mobile_phone} onChange={v => set('mobile_phone', v)} placeholder="(555) 123-4569" testId="profile-mobile-phone" type="tel" />
            </div>
          </Section>

          {/* Company Information */}
          <Section icon={Building2} title="Company Information" subtitle="Organization details and primary address">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Field label="Company Name" value={formData.company_name} onChange={v => set('company_name', v)} placeholder="Your Company Inc." testId="profile-company-name" />
              </div>
              <div className="md:col-span-2">
                <Field label="Street Address" value={formData.company_address} onChange={v => set('company_address', v)} placeholder="123 Business Park Drive, Suite 400" testId="profile-address" />
              </div>
              <Field label="City" value={formData.company_city} onChange={v => set('company_city', v)} placeholder="Austin" testId="profile-city" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="State" value={formData.company_state} onChange={v => set('company_state', v)} placeholder="TX" testId="profile-state" />
                <Field label="ZIP Code" value={formData.company_zip} onChange={v => set('company_zip', v)} placeholder="78701" testId="profile-zip" />
              </div>
            </div>
          </Section>

          {/* Employee Breakdown */}
          <Section icon={Users} title="Employee Information" subtitle="Workforce size and breakdown by category">
            <div className="grid md:grid-cols-4 gap-6">
              <Field label="Total Employees" value={formData.employee_count} onChange={v => set('employee_count', v)} placeholder="185" testId="profile-employee-count" />
              <Field label="In-Office" value={formData.employees_in_office} onChange={v => set('employees_in_office', v)} placeholder="80" testId="profile-employees-office" />
              <Field label="Remote" value={formData.employees_remote} onChange={v => set('employees_remote', v)} placeholder="60" testId="profile-employees-remote" />
              <Field label="Hybrid" value={formData.employees_hybrid} onChange={v => set('employees_hybrid', v)} placeholder="45" testId="profile-employees-hybrid" />
            </div>
          </Section>

          {/* Office Locations */}
          <Section icon={MapPin} title="Office Locations" subtitle="All company offices and facilities" iconColor="text-emerald-600" bgColor="bg-emerald-50">
            {officeLocations.map((loc, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-4 mb-4 bg-slate-50/50" data-testid={`office-location-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">Location {idx + 1}</span>
                  <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-8 w-8 p-0" onClick={() => setOfficeLocations(officeLocations.filter((_, i) => i !== idx))} data-testid={`remove-location-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <Field label="Location Name" value={loc.name} onChange={v => updateLocation(idx, 'name', v)} placeholder="e.g., Headquarters" />
                  <Field label="Type" value={loc.type} onChange={v => updateLocation(idx, 'type', v)} placeholder="HQ / Branch / Remote Hub" />
                  <Field label="Address" value={loc.address} onChange={v => updateLocation(idx, 'address', v)} placeholder="123 Main St" />
                  <Field label="City" value={loc.city} onChange={v => updateLocation(idx, 'city', v)} placeholder="Austin" />
                  <Field label="State" value={loc.state} onChange={v => updateLocation(idx, 'state', v)} placeholder="TX" />
                  <Field label="ZIP" value={loc.zip} onChange={v => updateLocation(idx, 'zip', v)} placeholder="78701" />
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={() => setOfficeLocations([...officeLocations, { ...EMPTY_LOCATION }])} className="flex items-center gap-2" data-testid="add-location-btn">
              <Plus className="h-4 w-4" /> Add Office Location
            </Button>
          </Section>

          {/* Key Contacts */}
          <Section icon={Briefcase} title="Key Contacts" subtitle="Important personnel for compliance and BCP roles" iconColor="text-blue-600" bgColor="bg-blue-50">
            {keyContacts.map((contact, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-4 mb-4 bg-slate-50/50" data-testid={`key-contact-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">Contact {idx + 1}</span>
                  <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-8 w-8 p-0" onClick={() => setKeyContacts(keyContacts.filter((_, i) => i !== idx))} data-testid={`remove-contact-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <Field label="Full Name" value={contact.name} onChange={v => updateContact(idx, 'name', v)} placeholder="Jane Doe" />
                  <Field label="Title" value={contact.title} onChange={v => updateContact(idx, 'title', v)} placeholder="VP of Operations" />
                  <Field label="Department" value={contact.department} onChange={v => updateContact(idx, 'department', v)} placeholder="Operations" />
                  <Field label="Email" value={contact.email} onChange={v => updateContact(idx, 'email', v)} placeholder="jane@company.com" type="email" />
                  <Field label="Phone" value={contact.phone} onChange={v => updateContact(idx, 'phone', v)} placeholder="(555) 123-4567" type="tel" />
                  <Field label="BCP/Compliance Role" value={contact.bcp_role} onChange={v => updateContact(idx, 'bcp_role', v)} placeholder="e.g., BCP Coordinator, IT Recovery Lead" />
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={() => setKeyContacts([...keyContacts, { ...EMPTY_CONTACT }])} className="flex items-center gap-2" data-testid="add-contact-btn">
              <Plus className="h-4 w-4" /> Add Key Contact
            </Button>
          </Section>

          {/* Departments */}
          <Section icon={Layers} title="Departments" subtitle="Add departments to delegate compliance template fields (max 10)" iconColor="text-teal-600" bgColor="bg-teal-50">
            {departments.map((dept, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-4 mb-4 bg-slate-50/50" data-testid={`department-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">Department {idx + 1}</span>
                  <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-8 w-8 p-0" onClick={() => setDepartments(departments.filter((_, i) => i !== idx))} data-testid={`remove-department-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <Field label="Department Name" value={dept.name} onChange={v => updateDepartment(idx, 'name', v)} placeholder="e.g., Legal, HR, Development" testId={`dept-name-${idx}`} />
                  <Field label="Description" value={dept.description} onChange={v => updateDepartment(idx, 'description', v)} placeholder="e.g., Handles legal compliance questions" testId={`dept-desc-${idx}`} />
                  <Field label="Notification Email" value={dept.email} onChange={v => updateDepartment(idx, 'email', v)} placeholder="legal@company.com" type="email" testId={`dept-email-${idx}`} />
                </div>
              </div>
            ))}
            {departments.length < 10 ? (
              <Button type="button" variant="outline" onClick={() => setDepartments([...departments, { ...EMPTY_DEPARTMENT }])} className="flex items-center gap-2" data-testid="add-department-btn">
                <Plus className="h-4 w-4" /> Add Department
              </Button>
            ) : (
              <p className="text-xs text-slate-400">Maximum of 10 departments reached.</p>
            )}
          </Section>

          {/* MSP/Vendor Information */}
          <Section icon={Server} title="MSP/IT Vendor Information" subtitle="This information will be auto-populated in compliance templates" iconColor="text-purple-600" bgColor="bg-purple-50">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">Vendor Portal Email</span>
              </div>
              <p className="text-xs text-purple-600 mb-3">This email receives vendor access links when you share compliance document sections. If left blank, the MSP Primary Contact Email below will be used.</p>
              <Field label="Vendor Email" value={formData.vendor_email} onChange={v => set('vendor_email', v)} placeholder="vendor@partner.com" testId="profile-vendor-email" type="email" />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="MSP/Vendor Company Name" value={formData.msp_company_name} onChange={v => set('msp_company_name', v)} placeholder="e.g., AuditGuard" testId="profile-msp-company" />
              <Field label="Contract/Account Number" value={formData.msp_contract_number} onChange={v => set('msp_contract_number', v)} placeholder="e.g., KTG-2024-001" testId="profile-msp-contract" />
              <Field label="Primary Contact Name" value={formData.msp_primary_contact} onChange={v => set('msp_primary_contact', v)} placeholder="Account Manager Name" testId="profile-msp-contact" />
              <Field label="Primary Contact Phone" value={formData.msp_primary_phone} onChange={v => set('msp_primary_phone', v)} placeholder="(555) 123-4567" testId="profile-msp-phone" type="tel" />
              <Field label="Primary Contact Email" value={formData.msp_primary_email} onChange={v => set('msp_primary_email', v)} placeholder="contact@msp.com" testId="profile-msp-email" type="email" />
              <Field label="24/7 Support Phone" value={formData.msp_support_phone} onChange={v => set('msp_support_phone', v)} placeholder="(555) 999-9999" testId="profile-msp-support-phone" type="tel" />
              <Field label="Support Email" value={formData.msp_support_email} onChange={v => set('msp_support_email', v)} placeholder="support@msp.com" testId="profile-msp-support-email" type="email" />
              <Field label="Support Portal URL" value={formData.msp_portal_url} onChange={v => set('msp_portal_url', v)} placeholder="https://support.msp.com" testId="profile-msp-portal" type="url" />
            </div>
          </Section>

          {/* Save */}
          <div className="flex justify-end">
            <Button type="submit" className="btn-primary px-8 py-3" disabled={loading} data-testid="save-profile-btn">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Profile</>}
            </Button>
          </div>
        </form>

        {/* Account Security - Outside main form */}
        <div className="space-y-6 mt-6">
          <Section icon={Shield} title="Account Security" subtitle="Change your email address or password" iconColor="text-blue-600" bgColor="bg-blue-50">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Change Email */}
              <form onSubmit={handleChangeEmail} className="space-y-4" data-testid="change-email-form">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Change Email
                </h3>
                <p className="text-xs text-slate-500">Current: <strong>{user?.email}</strong></p>
                <div className="space-y-1.5">
                  <Label className="text-sm">New Email</Label>
                  <Input type="email" value={emailForm.new_email} onChange={e => setEmailForm(f => ({...f, new_email: e.target.value}))} placeholder="newemail@company.com" data-testid="change-email-input" required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Confirm Password</Label>
                  <Input type="password" value={emailForm.password} onChange={e => setEmailForm(f => ({...f, password: e.target.value}))} placeholder="Enter current password" data-testid="change-email-pw" required />
                </div>
                <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5" disabled={emailSaving} data-testid="change-email-submit">
                  {emailSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                  {emailSaving ? 'Updating...' : 'Update Email'}
                </Button>
              </form>

              {/* Change Password */}
              <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <KeyRound className="h-4 w-4" /> Change Password
                </h3>
                <div className="space-y-1.5">
                  <Label className="text-sm">Current Password</Label>
                  <Input type="password" value={pwForm.current_password} onChange={e => setPwForm(f => ({...f, current_password: e.target.value}))} placeholder="Enter current password" data-testid="change-pw-current" required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">New Password</Label>
                  <Input type="password" value={pwForm.new_password} onChange={e => setPwForm(f => ({...f, new_password: e.target.value}))} placeholder="Min 8 characters" data-testid="change-pw-new" required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Confirm New Password</Label>
                  <Input type="password" value={pwForm.confirm_password} onChange={e => setPwForm(f => ({...f, confirm_password: e.target.value}))} placeholder="Confirm new password" data-testid="change-pw-confirm" required />
                </div>
                <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5" disabled={pwSaving} data-testid="change-pw-submit">
                  {pwSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
                  {pwSaving ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </div>
          </Section>

          {/* MFA Section */}
          <MFASection token={token} user={user} />
        </div>
      </div>
    </ClientLayout>
  );
}

/* MFA Settings Component */
function MFASection({ token, user }) {
  const [searchParams] = useSearchParams();
  const [mfaStatus, setMfaStatus] = useState({ mfa_enabled: false, mfa_method: null });
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(null); // null | 'choose' | 'totp' | 'email'
  const [setupData, setSetupData] = useState(null); // QR code data for TOTP
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  const isAdmin = user?.is_admin;
  const autoSetup = searchParams.get('setup_mfa') === 'true';

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    if (autoSetup && !mfaStatus.mfa_enabled && !loading) {
      setSetupMode('choose');
    }
  }, [autoSetup, mfaStatus.mfa_enabled, loading]);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API_URL}/auth/mfa/status`, { headers: { Authorization: `Bearer ${token}` }});
      setMfaStatus(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const startSetup = async (method) => {
    setSetupMode(method);
    try {
      const r = await axios.post(`${API_URL}/auth/mfa/setup`, { method }, { headers: { Authorization: `Bearer ${token}` }});
      setSetupData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start MFA setup');
      setSetupMode('choose');
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    if (!verifyCode.trim()) { toast.error('Enter a verification code'); return; }
    setVerifying(true);
    try {
      const r = await axios.post(`${API_URL}/auth/mfa/verify-setup`, { code: verifyCode.trim() }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(r.data.message);
      setBackupCodes(r.data.backup_codes);
      setMfaStatus({ mfa_enabled: true, mfa_method: setupMode === 'totp' ? 'totp' : 'email' });
      setVerifyCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setVerifying(false); }
  };

  const disableMfa = async (e) => {
    e.preventDefault();
    if (!disablePassword) { toast.error('Enter your password'); return; }
    setDisabling(true);
    try {
      await axios.post(`${API_URL}/auth/mfa/disable`, { password: disablePassword }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success('MFA has been disabled');
      setMfaStatus({ mfa_enabled: false, mfa_method: null });
      setShowDisable(false);
      setDisablePassword('');
      setSetupMode(null);
      setSetupData(null);
      setBackupCodes(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable MFA');
    } finally { setDisabling(false); }
  };

  const copyBackupCodes = () => {
    if (backupCodes) {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      toast.success('Backup codes copied to clipboard');
    }
  };

  if (loading) return null;

  return (
    <Section icon={ShieldCheck} title="Two-Factor Authentication" subtitle={isAdmin ? 'Required for admin accounts' : 'Add an extra layer of security to your account'} iconColor="text-emerald-600" bgColor="bg-emerald-50">
      {/* Backup codes display */}
      {backupCodes && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid="backup-codes-display">
          <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Save Your Backup Codes
          </h3>
          <p className="text-xs text-amber-700 mb-3">Store these codes in a safe place. Each code can only be used once to sign in if you lose access to your authentication method.</p>
          <div className="bg-white rounded-lg p-4 border border-amber-200 font-mono text-sm grid grid-cols-2 gap-2">
            {backupCodes.map((code, i) => (
              <span key={i} className="text-slate-800" data-testid={`backup-code-${i}`}>{code}</span>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" className="mt-3 gap-1.5" onClick={copyBackupCodes} data-testid="copy-backup-codes-btn">
            <Copy className="h-3.5 w-3.5" /> Copy All Codes
          </Button>
          <Button type="button" variant="ghost" size="sm" className="mt-3 ml-2 text-amber-700" onClick={() => setBackupCodes(null)} data-testid="dismiss-backup-codes-btn">
            I've saved them
          </Button>
        </div>
      )}

      {mfaStatus.mfa_enabled && !setupMode ? (
        /* MFA is enabled */
        <div data-testid="mfa-enabled-status">
          <div className="flex items-center gap-3 mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <ShieldCheck className="h-6 w-6 text-emerald-600" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">MFA is Enabled</p>
              <p className="text-xs text-emerald-600">
                Method: {mfaStatus.mfa_method === 'totp' ? 'Authenticator App' : 'Email Verification'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <Button type="button" variant="outline" size="sm" className="text-[#FF6B35] border-[#FF6B35]/30 hover:bg-[#FF6B35]/5 gap-1.5" onClick={() => setSetupMode('choose')} data-testid="change-mfa-method-btn">
                <RefreshCw className="h-3.5 w-3.5" /> Change Method
              </Button>
            )}

            {!isAdmin && !showDisable && (
              <Button type="button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5" onClick={() => setShowDisable(true)} data-testid="show-disable-mfa-btn">
                <ShieldOff className="h-3.5 w-3.5" /> Disable MFA
              </Button>
            )}
          </div>

          {showDisable && (
            <form onSubmit={disableMfa} className="mt-4 p-4 border border-red-200 rounded-lg bg-red-50/50 space-y-3" data-testid="disable-mfa-form">
              <p className="text-sm text-red-700">Enter your password to disable MFA:</p>
              <Input
                type="password"
                value={disablePassword}
                onChange={e => setDisablePassword(e.target.value)}
                placeholder="Current password"
                data-testid="disable-mfa-password"
                required
              />
              <div className="flex gap-2">
                <Button type="submit" size="sm" className="bg-red-600 hover:bg-red-700 text-white gap-1.5" disabled={disabling} data-testid="confirm-disable-mfa-btn">
                  {disabling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldOff className="h-3.5 w-3.5" />}
                  {disabling ? 'Disabling...' : 'Confirm Disable'}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowDisable(false); setDisablePassword(''); }}>Cancel</Button>
              </div>
            </form>
          )}
        </div>
      ) : !setupMode || setupMode === 'choose' ? (
        /* Choose MFA method */
        <div data-testid="mfa-choose-method">
          {isAdmin && !mfaStatus.mfa_enabled && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 font-medium">Admin accounts are required to enable MFA. Please choose a method below.</p>
            </div>
          )}
          <p className="text-sm text-slate-600 mb-4">Choose how you'd like to verify your identity:</p>
          <div className="grid md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => startSetup('totp')}
              className="p-5 border-2 border-slate-200 rounded-xl hover:border-[#FF6B35] hover:bg-[#FF6B35]/5 transition-all text-left group"
              data-testid="choose-totp-btn"
            >
              <Smartphone className="h-8 w-8 text-slate-400 group-hover:text-[#FF6B35] mb-3 transition-colors" />
              <h4 className="font-semibold text-slate-800 text-sm">Authenticator App</h4>
              <p className="text-xs text-slate-500 mt-1">Use Google Authenticator, Authy, or any TOTP app. More secure.</p>
            </button>
            <button
              type="button"
              onClick={() => startSetup('email')}
              className="p-5 border-2 border-slate-200 rounded-xl hover:border-[#FF6B35] hover:bg-[#FF6B35]/5 transition-all text-left group"
              data-testid="choose-email-btn"
            >
              <Mail className="h-8 w-8 text-slate-400 group-hover:text-[#FF6B35] mb-3 transition-colors" />
              <h4 className="font-semibold text-slate-800 text-sm">Email Verification</h4>
              <p className="text-xs text-slate-500 mt-1">Receive a code via email each time you sign in. Convenient.</p>
            </button>
          </div>
        </div>
      ) : (
        /* Setup flow */
        <div data-testid="mfa-setup-flow">
          {setupMode === 'totp' && setupData?.qr_code && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">1. Scan this QR code with your authenticator app</h3>
              <div className="flex flex-col items-center gap-4 p-6 bg-white border border-slate-200 rounded-xl">
                <img
                  src={`data:image/png;base64,${setupData.qr_code}`}
                  alt="MFA QR Code"
                  className="w-48 h-48"
                  data-testid="mfa-qr-code"
                />
                <div className="text-center">
                  <p className="text-xs text-slate-400 mb-1">Or enter this key manually:</p>
                  <code className="text-xs bg-slate-100 px-3 py-1.5 rounded font-mono select-all" data-testid="mfa-secret-key">{setupData.secret}</code>
                </div>
              </div>
              <h3 className="text-sm font-semibold text-slate-700 mt-5 mb-2">2. Enter the 6-digit code from your app</h3>
            </div>
          )}

          {setupMode === 'email' && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Enter the verification code sent to your email</h3>
              <p className="text-xs text-slate-500 mb-4">Check your inbox for a 6-digit code from AuditGuard.</p>
            </div>
          )}

          <form onSubmit={verifySetup} className="space-y-4" data-testid="mfa-setup-verify-form">
            <Input
              type="text"
              inputMode="numeric"
              maxLength={8}
              value={verifyCode}
              onChange={e => setVerifyCode(e.target.value.replace(/\s/g, ''))}
              placeholder="Enter code"
              className="h-12 text-center text-xl tracking-[0.2em] font-mono max-w-xs"
              autoFocus
              data-testid="mfa-setup-code-input"
              required
            />
            <div className="flex gap-2">
              <Button type="submit" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" disabled={verifying} data-testid="mfa-setup-verify-btn">
                {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                {verifying ? 'Verifying...' : 'Enable MFA'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setSetupMode(null); setSetupData(null); setVerifyCode(''); }} data-testid="mfa-setup-cancel-btn">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </Section>
  );
}

/* Reusable section wrapper */
function Section({ icon: Icon, title, subtitle, iconColor = 'text-[#FF6B35]', bgColor = 'bg-[#FF6B35]/10', children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

/* Reusable field */
function Field({ label, value, onChange, placeholder, type = 'text', disabled = false, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Input
        type={type}
        value={value || ''}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        placeholder={placeholder}
        disabled={disabled}
        className={disabled ? 'bg-slate-50' : ''}
        {...(testId ? { 'data-testid': testId } : {})}
      />
    </div>
  );
}
